import React, { useState, useRef, useEffect, useCallback } from "react";
import { QRCodeSVG } from "qrcode.react";
import toast from "react-hot-toast";
import { SUploadQRLogo, SDeleteQRLogo } from "../../apis/app.service";
import "./core/styles.scss";

interface QREntry {
  id: string;
  label: string;
  value: string;
  fgColor: string;
  bgColor: string;
  logoUrl?: string;
  createdAt: string;
}

const STORAGE_KEY = "sema_qrcodes";

function loadEntries(): QREntry[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
  } catch {
    return [];
  }
}

function saveEntries(entries: QREntry[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
}

function isValidHex(hex: string) {
  return /^#[0-9A-Fa-f]{6}$/.test(hex);
}

const DEFAULT_FORM = {
  label: "",
  value: "",
  fgColor: "#000000",
  bgColor: "#FFFFFF",
  logoUrl: "",
};

export default function QRCodePage() {
  const [entries, setEntries] = useState<QREntry[]>(loadEntries);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [fgHex, setFgHex] = useState("#000000");
  const [bgHex, setBgHex] = useState("#FFFFFF");
  const [uploading, setUploading] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const selected = entries.find((e) => e.id === selectedId) ?? null;

  useEffect(() => {
    if (selected && !isNew) {
      const next = {
        label: selected.label,
        value: selected.value,
        fgColor: selected.fgColor,
        bgColor: selected.bgColor,
        logoUrl: selected.logoUrl ?? "",
      };
      setForm(next);
      setFgHex(selected.fgColor);
      setBgHex(selected.bgColor);
    }
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFgChange = useCallback((val: string) => {
    setFgHex(val);
    if (isValidHex(val)) setForm((f) => ({ ...f, fgColor: val }));
  }, []);

  const handleBgChange = useCallback((val: string) => {
    setBgHex(val);
    if (isValidHex(val)) setForm((f) => ({ ...f, bgColor: val }));
  }, []);

  function handleNew() {
    setSelectedId(null);
    setIsNew(true);
    setForm(DEFAULT_FORM);
    setFgHex("#000000");
    setBgHex("#FFFFFF");
  }

  function handleSelect(id: string) {
    setIsNew(false);
    setSelectedId(id);
  }

  async function handleLogoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await SUploadQRLogo(file);
      setForm((f) => ({ ...f, logoUrl: url }));
      toast.success("Logo uploaded.");
    } catch (err: any) {
      toast.error(err.message ?? "Upload failed.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  function handleRemoveLogo() {
    setForm((f) => ({ ...f, logoUrl: "" }));
  }

  function handleSave() {
    if (!form.label.trim() || !form.value.trim()) {
      toast.error("Label and content are required.");
      return;
    }

    if (isNew) {
      const entry: QREntry = {
        id: crypto.randomUUID(),
        label: form.label.trim(),
        value: form.value.trim(),
        fgColor: form.fgColor,
        bgColor: form.bgColor,
        logoUrl: form.logoUrl || undefined,
        createdAt: new Date().toISOString(),
      };
      const updated = [entry, ...entries];
      setEntries(updated);
      saveEntries(updated);
      setIsNew(false);
      setSelectedId(entry.id);
      toast.success("QR code saved.");
    } else if (selectedId) {
      const updated = entries.map((e) =>
        e.id === selectedId
          ? {
              ...e,
              label: form.label.trim(),
              value: form.value.trim(),
              fgColor: form.fgColor,
              bgColor: form.bgColor,
              logoUrl: form.logoUrl || undefined,
            }
          : e
      );
      setEntries(updated);
      saveEntries(updated);
      toast.success("QR code updated.");
    }
  }

  function handleDelete() {
    if (!selectedId) return;
    const entry = entries.find((e) => e.id === selectedId);
    if (entry?.logoUrl) {
      SDeleteQRLogo(entry.logoUrl).catch(() => {});
    }
    const updated = entries.filter((e) => e.id !== selectedId);
    setEntries(updated);
    saveEntries(updated);
    setSelectedId(null);
    setIsNew(false);
    setForm(DEFAULT_FORM);
    setFgHex("#000000");
    setBgHex("#FFFFFF");
    toast.success("QR code deleted.");
  }

  async function handleDownload() {
    const svgEl = previewRef.current?.querySelector("svg");
    if (!svgEl) return;

    // Clone so we can mutate without affecting the visible preview
    const clone = svgEl.cloneNode(true) as SVGElement;

    // Replace any external <image> hrefs with data URLs so the canvas stays untainted
    const imgEls = clone.querySelectorAll("image");
    await Promise.all(
      Array.from(imgEls).map(async (img) => {
        const href = img.getAttribute("href") || img.getAttribute("xlink:href");
        if (!href || href.startsWith("data:")) return;
        try {
          const resp = await fetch(href);
          const blob = await resp.blob();
          const dataUrl = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.readAsDataURL(blob);
          });
          img.setAttribute("href", dataUrl);
          img.removeAttribute("xlink:href");
        } catch {
          img.remove();
        }
      })
    );

    const SIZE = 512;
    const svgData = new XMLSerializer().serializeToString(clone);
    const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
    const svgUrl = URL.createObjectURL(svgBlob);

    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = SIZE;
      canvas.height = SIZE;
      canvas.getContext("2d")!.drawImage(image, 0, 0, SIZE, SIZE);
      URL.revokeObjectURL(svgUrl);
      const a = document.createElement("a");
      a.href = canvas.toDataURL("image/png");
      a.download = `${(form.label || "qrcode").replace(/\s+/g, "-").toLowerCase()}.png`;
      a.click();
    };
    image.src = svgUrl;
  }

  const previewValue = form.value.trim() || "https://example.com";
  const isEditing = isNew || !!selectedId;

  const logoSettings = form.logoUrl
    ? { src: form.logoUrl, height: 48, width: 48, excavate: true }
    : undefined;

  // Download canvas needs a higher error correction level when a logo is present
  const ecLevel = form.logoUrl ? "H" : "M";

  return (
    <div className="qrcode-page">
      <div className="qrcode-container">
        <div className="qrcode-header">
          <h2>QR Codes</h2>
          <button className="btn btn-primary" onClick={handleNew}>
            <i className="bx bx-plus"></i>
            New QR Code
          </button>
        </div>

        <div className="qrcode-content">
          {/* ── Left: saved list ── */}
          <div className="qrcode-list-panel">
            <div className="list-header">
              <h3>Saved ({entries.length})</h3>
            </div>

            {entries.length === 0 ? (
              <div className="no-qrcodes">
                <i className="bx bx-qr"></i>
                <p>No QR codes yet.</p>
                <p>Click "New QR Code" to create one.</p>
              </div>
            ) : (
              <ul className="qrcode-items">
                {entries.map((entry) => (
                  <li
                    key={entry.id}
                    className={`qrcode-item ${selectedId === entry.id && !isNew ? "active" : ""}`}
                    onClick={() => handleSelect(entry.id)}
                  >
                    <div className="item-preview">
                      <QRCodeSVG
                        value={entry.value}
                        size={36}
                        fgColor={entry.fgColor}
                        bgColor={entry.bgColor}
                        level="L"
                      />
                    </div>
                    <div className="item-info">
                      <h4>{entry.label}</h4>
                      <p>{entry.value}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* ── Right: editor + preview ── */}
          <div className="qrcode-editor-panel">
            {isEditing ? (
              <>
                {/* Form */}
                <div className="qrcode-form">
                  <h3>{isNew ? "New QR Code" : "Edit QR Code"}</h3>

                  <div className="form-group">
                    <label>Label</label>
                    <input
                      type="text"
                      placeholder="e.g. Feedback Form, Event Booth A…"
                      value={form.label}
                      onChange={(e) => setForm({ ...form, label: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label>URL or Text</label>
                    <textarea
                      placeholder="https://… or any text"
                      value={form.value}
                      onChange={(e) => setForm({ ...form, value: e.target.value })}
                    />
                  </div>

                  {/* Colors */}
                  <div className="form-group">
                    <label>Colors</label>
                    <div className="color-row">
                      <div className="color-field">
                        <input
                          type="color"
                          value={form.fgColor}
                          onChange={(e) => {
                            setForm({ ...form, fgColor: e.target.value });
                            setFgHex(e.target.value);
                          }}
                        />
                        <div className="color-text-wrap">
                          <span className="color-field-label">Foreground</span>
                          <input
                            type="text"
                            className="hex-input"
                            maxLength={7}
                            value={fgHex}
                            onChange={(e) => handleFgChange(e.target.value)}
                            placeholder="#000000"
                          />
                        </div>
                      </div>
                      <div className="color-field">
                        <input
                          type="color"
                          value={form.bgColor}
                          onChange={(e) => {
                            setForm({ ...form, bgColor: e.target.value });
                            setBgHex(e.target.value);
                          }}
                        />
                        <div className="color-text-wrap">
                          <span className="color-field-label">Background</span>
                          <input
                            type="text"
                            className="hex-input"
                            maxLength={7}
                            value={bgHex}
                            onChange={(e) => handleBgChange(e.target.value)}
                            placeholder="#FFFFFF"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Center logo */}
                  <div className="form-group">
                    <label>Center Logo</label>
                    {form.logoUrl ? (
                      <div className="logo-preview-row">
                        <img src={form.logoUrl} alt="logo" className="logo-thumb" />
                        <button
                          className="btn btn-danger"
                          onClick={handleRemoveLogo}
                          type="button"
                        >
                          <i className="bx bx-trash"></i>
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div className="logo-upload-area" onClick={() => fileInputRef.current?.click()}>
                        {uploading ? (
                          <span className="upload-hint">Uploading…</span>
                        ) : (
                          <>
                            <i className="bx bx-image-add"></i>
                            <span className="upload-hint">Click to upload PNG, JPG or SVG (max 5 MB)</span>
                          </>
                        )}
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                          style={{ display: "none" }}
                          onChange={handleLogoUpload}
                        />
                      </div>
                    )}
                  </div>

                  <div className="form-actions">
                    <button className="btn btn-primary" onClick={handleSave} disabled={uploading}>
                      <i className="bx bx-save"></i>
                      {isNew ? "Save" : "Update"}
                    </button>
                    {!isNew && (
                      <button className="btn btn-danger" onClick={handleDelete}>
                        <i className="bx bx-trash"></i>
                        Delete
                      </button>
                    )}
                    {isNew && (
                      <button
                        className="btn btn-outline"
                        onClick={() => {
                          setIsNew(false);
                          setSelectedId(null);
                          setForm(DEFAULT_FORM);
                          setFgHex("#000000");
                          setBgHex("#FFFFFF");
                        }}
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>

                {/* Preview */}
                <div className="qrcode-preview">
                  <h4>Preview</h4>

                  <div className="preview-qr" style={{ borderColor: form.fgColor }} ref={previewRef}>
                    <QRCodeSVG
                      value={previewValue}
                      size={180}
                      fgColor={form.fgColor}
                      bgColor={form.bgColor}
                      level={ecLevel}
                      imageSettings={logoSettings}
                    />
                  </div>

                  {form.label && <p className="preview-label">{form.label}</p>}
                  {form.value && <p className="preview-url">{form.value}</p>}

                  <div className="preview-download">
                    <button className="btn btn-secondary btn-full" onClick={handleDownload}>
                      <i className="bx bx-download"></i>
                      Download PNG
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div style={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#ccc", flexDirection: "column", gap: "0.75rem" }}>
                <i className="bx bx-qr" style={{ fontSize: "4rem" }}></i>
                <p style={{ fontSize: "1rem", margin: 0 }}>Select a QR code or create a new one</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
